import { Link } from 'react-router-dom'
import { useDashboard, useMentorPerformance, useStorageUsage } from '@/lib/queries'
import { PageHeader, Card, PageStack } from '@/components/ui/Page'
import { Table } from '@/components/ui/Table'
import type { Column } from '@/components/ui/Table'
import { Pill } from '@/components/ui/Pill'
import {
  formatBytes,
  formatMoney,
  formatMoneyCompact,
  formatNumber,
  formatRating,
} from '@/lib/format'
import type { MentorPerformance } from '@/types/api'
import styles from './DashboardPage.module.css'

/**
 * A2 — Dashboard.
 *
 * Ordered by what an admin can ACT on, not by what is easiest to compute.
 * "Needs attention" sits first because a mentor stuck with an incomplete
 * profile is invisible to students and nobody finds out unless it is surfaced.
 */
export function DashboardPage() {
  const { data, isLoading, error } = useDashboard()
  const performance = useMentorPerformance(8)
  const storage = useStorageUsage()

  /*
    *** WHAT THIS CLIENT SELLS DECIDES WHAT THIS SCREEN SAYS ***
    Revenue, plans and quotas all mean something different per model, and a
    per-session tenant has no plans at all. Defaults to false while loading,
    so the screen never flashes subscription wording at a per-session client.
  */
  const byPlan = data?.billing_mode === 'SUBSCRIPTION'

  if (error) {
    return (
      <>
        <PageHeader title="Dashboard" />
        <Card>
          <p className={styles.errorText}>
            Could not load the dashboard. {error.message}
          </p>
        </Card>
      </>
    )
  }

  const attention = [
    {
      count: data?.mentors_awaiting_profile ?? 0,
      label: 'mentors have not completed their profile',
      // The consequence, not the state. "Incomplete profile" means nothing to
      // someone who does not know it gates discovery.
      detail: 'Students cannot see or book them until they do.',
      to: '/mentors',
      action: 'Review mentors',
    },
    {
      count: data?.pending_payment_bookings ?? 0,
      label: 'bookings are waiting on payment',
      detail: 'These slots are held and unavailable to other students.',
      to: '/bookings?status=PENDING_PAYMENT',
      action: 'View bookings',
    },
    /*
      *** RENEWALS ARE ONLY ACTIONABLE IF SEEN BEFORE THEY LAPSE ***
      A plan expiring within the week is the one thing on a subscription
      tenant an admin can still do something about. Afterwards the student
      is simply blocked, and the first anyone hears of it is a complaint.
      Zero-count items are filtered out below, so this is absent on a
      per-session tenant — where the server sends 0 by definition.
    */
    {
      count: byPlan ? (data?.subscriptions_expiring_soon ?? 0) : 0,
      label: 'plans expire within a week',
      detail: 'Students lose access when their plan ends unless they renew.',
      to: '/students',
      action: 'View students',
    },
  ].filter((item) => item.count > 0)

  return (
    <PageStack>
      <PageHeader
        title="Dashboard"
        description="Activity across your organisation."
      />

      {attention.length > 0 && (
        <div className={styles.attention}>
          {attention.map((item) => (
            <div key={item.to} className={styles.attentionCard}>
              <div className={styles.attentionCount}>
                <span className="numeric">{item.count}</span>
              </div>
              <div className={styles.attentionText}>
                <p className={styles.attentionLabel}>{item.label}</p>
                <p className={styles.attentionDetail}>{item.detail}</p>
              </div>
              <Link to={item.to} className={styles.attentionAction}>
                {item.action} →
              </Link>
            </div>
          ))}
        </div>
      )}

      <div className={styles.metricGrid}>
        {/*
          *** THE SAME NUMBER, NAMED FOR WHAT IT ACTUALLY COUNTS ***
          Revenue is captured payments either way. On a per-session tenant
          those are session fees. On a subscription tenant they are PLAN
          PURCHASES — so a month in which existing students spend sessions
          they already paid for shows ₹0.00 while the service is running
          perfectly normally. Labelling both "Revenue this month" made a
          healthy subscription client look broken.
        */}
        <Metric
          label={byPlan ? 'Plan sales this month' : 'Revenue this month'}
          value={
            data ? formatMoneyCompact(data.revenue_this_month, data.currency) : '—'
          }
          exact={data ? formatMoney(data.revenue_this_month, data.currency) : undefined}
          hint={
            byPlan
              ? 'Payments for plans bought this month'
              : 'Payments received, not amounts quoted'
          }
          loading={isLoading}
          accent="teal"
        />
        <Metric
          label="Sessions today"
          value={data ? formatNumber(data.bookings_today) : '—'}
          hint={
            data ? `${formatNumber(data.upcoming_bookings)} upcoming overall` : undefined
          }
          loading={isLoading}
          accent="indigo"
        />

        {/*
          *** PLANS REPLACE A TILE, THEY DO NOT ADD A FIFTH ***
          On a subscription tenant the health of the business is active plans
          and the sessions owed against them, not this month's takings. Shown
          INSTEAD of "Active students", whose count is already on the
          students screen — a five-tile row reads as clutter.
        */}
        {byPlan ? (
          <Metric
            label="Active plans"
            value={data ? formatNumber(data.active_subscriptions) : '—'}
            hint={
              data
                ? `${formatNumber(data.sessions_remaining)} sessions still owed`
                : undefined
            }
            loading={isLoading}
          />
        ) : (
          <Metric
            label="Active students"
            value={data ? formatNumber(data.active_students) : '—'}
            hint={data ? `${formatNumber(data.total_students)} registered` : undefined}
            loading={isLoading}
          />
        )}

        <Metric
          label="Bookable mentors"
          value={data ? formatNumber(data.visible_mentors) : '—'}
          hint={data ? `${formatNumber(data.total_mentors)} total` : undefined}
          loading={isLoading}
        />
      </div>

      <div className={styles.twoColumn}>
        <Card
          title="This month"
          description="Bookings and money, month to date."
        >
          <dl className={styles.statList}>
            <Stat
              label="Bookings created"
              value={data ? formatNumber(data.bookings_this_month) : '—'}
            />
            <Stat
              label="Completed"
              value={data ? formatNumber(data.completed_bookings) : '—'}
            />
            <Stat
              label="Cancelled"
              value={data ? formatNumber(data.cancelled_bookings) : '—'}
            />
            {/* Plans still live, and how many sessions they still owe.
                A subscription tenant's month is measured in delivery, not
                takings — and these are meaningless on a per-session tenant,
                where three zeroes would be worse than nothing. */}
            {byPlan && (
              <>
                <Stat
                  label="Active plans"
                  value={data ? formatNumber(data.active_subscriptions) : '—'}
                />
                <Stat
                  label="Sessions still owed"
                  value={data ? formatNumber(data.sessions_remaining) : '—'}
                  hint="Paid for and not yet used"
                />
              </>
            )}
            <Stat
              label={byPlan ? 'Plan sales (all time)' : 'Revenue (all time)'}
              value={
                data ? formatMoney(data.revenue_all_time, data.currency) : '—'
              }
            />
            <Stat
              label="Refunds this month"
              value={
                data ? formatMoney(data.refunds_this_month, data.currency) : '—'
              }
              /* Shown separately rather than netted off revenue: netting hides
                 the refund rate, which is the number worth watching. */
              hint="Not deducted from revenue above"
            />
            <Stat
              label="Average rating"
              value={
                data?.average_rating
                  ? `${formatRating(data.average_rating)} / 5`
                  : 'No ratings yet'
              }
              hint={
                data?.feedback_count
                  ? `from ${formatNumber(data.feedback_count)} reviews`
                  : undefined
              }
            />
          </dl>
        </Card>

        <Card
          title="Attachment storage"
          description="Files shared in chat."
        >
          {storage.data ? (
            <dl className={styles.statList}>
              <Stat
                label="Files stored"
                value={formatNumber(storage.data.file_count)}
              />
              <Stat
                label="Total size"
                value={formatBytes(storage.data.database_bytes)}
              />
              {storage.data.disk_bytes !== undefined && (
                <Stat
                  label="On disk"
                  value={formatBytes(storage.data.disk_bytes)}
                  /* A gap between the database's view and the disk means
                     orphaned or missing files — worth knowing before someone
                     reports a broken download. */
                  hint={
                    storage.data.drift_bytes
                      ? `${formatBytes(Math.abs(storage.data.drift_bytes))} ${
                          storage.data.drift_bytes > 0
                            ? 'more on disk than recorded'
                            : 'missing from disk'
                        }`
                      : 'Matches the records'
                  }
                />
              )}
              <Stat label="Storage" value={storage.data.storage_provider} />
            </dl>
          ) : (
            <p className={styles.muted}>Loading storage usage…</p>
          )}
        </Card>
      </div>

      <Card
        title="Mentor activity"
        description="Ranked by completed sessions. Mentors with none are listed too."
        flush
      >
        <Table
          columns={MENTOR_COLUMNS}
          rows={performance.data?.mentors ?? []}
          rowKey={(row) => row.mentor_id}
          loading={performance.isLoading}
          error={performance.error}
          emptyMessage="No mentors yet. Add one to get started."
        />
      </Card>
    </PageStack>
  )
}

const MENTOR_COLUMNS: Array<Column<MentorPerformance>> = [
  {
    key: 'name',
    header: 'Mentor',
    render: (row) => <span className={styles.mentorName}>{row.full_name}</span>,
  },
  {
    key: 'sessions',
    header: 'Completed',
    align: 'right',
    render: (row) => (
      <span className="numeric">{formatNumber(row.completed_sessions)}</span>
    ),
  },
  {
    key: 'revenue',
    header: 'Revenue',
    align: 'right',
    render: (row) => <span className="numeric">{formatMoney(row.revenue)}</span>,
  },
  {
    key: 'rating',
    header: 'Rating',
    align: 'right',
    secondary: true,
    render: (row) =>
      row.average_rating ? (
        <span className="numeric">
          {formatRating(row.average_rating)}
          <span className={styles.ratingCount}> ({row.feedback_count})</span>
        </span>
      ) : (
        <Pill tone="neutral">No ratings</Pill>
      ),
  },
]

// ------------------------------------------------------------- sub-components
interface MetricProps {
  label: string
  value: string
  /** Full precision, shown on hover when the headline is abbreviated. */
  exact?: string
  hint?: string
  loading?: boolean
  accent?: 'indigo' | 'teal'
}

function Metric({ label, value, exact, hint, loading, accent }: MetricProps) {
  return (
    <div className={`${styles.metric} ${accent ? styles[accent] : ''}`}>
      <p className={styles.metricLabel}>{label}</p>
      {loading ? (
        <span className={styles.metricSkeleton} />
      ) : (
        <p className={`${styles.metricValue} numeric`} title={exact}>
          {value}
        </p>
      )}
      {hint && <p className={styles.metricHint}>{hint}</p>}
    </div>
  )
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string
  value: string
  hint?: string
}) {
  return (
    <div className={styles.stat}>
      <dt className={styles.statLabel}>
        {label}
        {hint && <span className={styles.statHint}>{hint}</span>}
      </dt>
      <dd className={`${styles.statValue} numeric`}>{value}</dd>
    </div>
  )
}
